import React, { useEffect, useMemo, useState } from 'react';
import Colorful from '@uiw/react-color-colorful';
import {
	hsvaToHex,
	hexToHsva,
	hexToRgba,
	rgbaToHex,
	getContrastingColor,
	validHex,
} from '@uiw/color-convert';
import { useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import Swatch from '@uiw/react-color-swatch';
import EditableInput from '@uiw/react-color-editable-input';
import { Box, Stack, TextField, Typography, Tooltip, Grid, useTheme } from '@mui/material';
import EditableInputRGBA from '@uiw/react-color-editable-input-rgba';
import ClearIcon from '@mui/icons-material/Clear';

const BrokerageColourPicker = ({ value = '', setValue, canRemove = true, ...props }) => {
	const theme = useTheme();
	const [textValue, setTextValue] = useState(value ?? '');

	const hsva = value && validHex(value) ? hexToHsva(value) : { h: 0, s: 0, v: 0, a: 0 };

	useEffect(() => {
		setTextValue(value ?? '');
	}, [value]);

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		}
	);

	const presetColours = useMemo(() => {
		const colours = [
			...Object.values(userDetails?.colours ?? {}),

			theme.palette.primary.main,
			'#4A90E2',
			'#50E3C2',
			'#7ED321',
			'#417505',
			'#8B572A',
			'#F8E71C',
			'#F5A623',
			'#D0021B',
			'#BD10E0',
			'#9013FE',

			'#9B9B9B',
			'#4A4A4A',
			'#000000',
			'#FFFFFF',
			...(canRemove ? ['transparent'] : []),
		];
		return Array.from(new Set(colours));
	}, [userDetails?.colours, canRemove, theme]);

	const editableInputProps = {
		renderInput: (props) => {
			return (
				<TextField
					{...props}
					sx={{
						'& input': {
							fontSize: '12px',
							padding: '4px 6px',
						},
					}}
				/>
			);
		},
		style: {
			alignItems: 'flex-start',
			border: 'none',
			padding: 0,
			'--editable-input-box-shadow': 'none',
		},
		labelStyle: {
			marginLeft: '6px',
		},
	};

	const colourString = (colour) => {
		// if (colour === '#00FFFFFF') {
		// 	return 'Transparent';
		// }
		const userColour = Object.entries(userDetails?.colours ?? {}).find(
			([key, colourValue]) => colourValue === colour
		);
		return userColour ? `${colour} (${userColour[0]})` : colour;
	};

	return (
		<>
			<Stack
				direction='column'
				width='200px'
				spacing={1}
				sx={{
					'& .w-color-saturation': {
						borderRadius: '4px 4px 0px 0px !important',
					},
					'& .w-color-alpha': {
						borderRadius: '0px 0px 4px 4px !important',
						'& > div:first-of-type': {
							borderRadius: '0px 0px 4px 4px !important',
						},
					},
					// '& .w-color-interactive': {
					// 	'& > div:first-of-type': {
					// 		height: '20px !important',
					// 		width: '20px !important',
					//         transform: 'translate(-10px, -10px)'
					// 	},
					// },
					// },
					// '& .w-color-interactive': {
					// 	'& div': {
					// 		'& div': {
					// 			borderRadius: '50% !important',
					// 		},
					// 	},
					// },
				}}
			>
				<Colorful
					color={hsva}
					onChange={(color) => {
						setValue(color.hex);
					}}
					disableAlpha
					style={{
						borderRadius: '4px 4px 0px 0px',
					}}
				/>
				<Stack direction='row'>
					<EditableInput
						label='Hex'
						value={textValue}
						onChange={(e, v) => {
							const newValue = v.toString().startsWith('#') ? v : `#${v}`;
							// setTextValue(v.toString());
							setTextValue(newValue);
							if (validHex(newValue) || validHex(`#${newValue}`)) {
								const newRgba = hexToRgba(newValue);
								console.log('COLOUR TEXT VALUE', { newValue, value: v, validHex: validHex(v), validHexHash: validHex(newValue), newRgba });
								// setValue(newValue);//v.toString().startsWith('#') ? v : `#${v}`);//validHex(v) ? v.toString() : `#${v}`);

								setValue(rgbaToHex(newRgba));
							}

						}}
						{...editableInputProps}
						style={{
							...editableInputProps.style,
							width: '100%',
						}}
					/>
					<EditableInputRGBA
						aProps={false}
						rProps={{
							...editableInputProps,
						}}
						gProps={{
							...editableInputProps,
						}}
						bProps={{
							...editableInputProps,
						}}
						hsva={hsva}
						onChange={(color) => {
							// setHsva({ ...hsva, ...color.hsva });
							setValue(color.hex);
						}}
					/>
				</Stack>
				<Stack
					direction='row'
					spacing={1}
					sx={{ justifyContent: 'space-between', alignItems: 'center' }}
				>
					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
						{presetColours.map((colour) => {
							return (
								<Tooltip
									arrow
									key={colour}
									disableInteractive
									title={colourString(colour)}
								>
									<Box
										key={colour}
										onClick={(e) => setValue(colour)}
										sx={{
											width: '16px',
											cursor: 'pointer',
											height: '16px',
											backgroundColor: colour,
											borderRadius: '4px',
											...(colour === value && {
												width: '18px',
												height: '18px',
												border: `2px solid ${theme.palette.divider}`,
												boxShadow: 3,
											}),
											border: `1.5px solid ${theme.palette.divider}`,
											'&:hover': {
												width: '18px',
												height: '18px',
												border: `2px solid ${theme.palette.divider}`,
												boxShadow: 3,
											},
										}}
									>
										{colour === 'transparent' && (
											<Box
												sx={{
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													height: '100%',
													width: '100%',
												}}
											>
												<ClearIcon sx={{ fontSize: '8px', padding: 0 }} />
											</Box>
										)}
									</Box>
								</Tooltip>
							);
						})}
					</Box>
					<Box
						sx={{
							display: 'flex',
							backgroundColor: value,
							justifyContent: 'center',
							alignItems: 'center',
							borderRadius: '4px',
						}}
					>
						<Box
							sx={{
								display: 'flex',
								backgroundColor: value,
								justifyContent: 'center',
								alignItems: 'center',
								borderRadius: '4px',
								padding: '4px',
								width: '50px',
								height: '50px',
								border: `1.5px solid ${theme.palette.divider}`,

								textAlign: 'center',
							}}
						>
							<Typography
								sx={{
									fontSize: '10px',
									color: getContrastingColor(
										validHex(value)
											? value
											: theme.palette.mode === 'dark'
												? theme.palette.darkModePaper
												: '#ffffff',
									),
								}}
							>
								{validHex(value) ? value : 'none'}
							</Typography>
						</Box>
					</Box>
				</Stack>
			</Stack>
		</>
	);
};

export default BrokerageColourPicker;
